import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'react-native-toast-notifications';

/**
 * Listens for 'graphql-server-error', 'graphql-circuit-breaker',
 * 'graphql-cloudflare-error', 'graphql-rate-limit', and
 * 'graphql-mutation-error' CustomEvents dispatched by useApolloClient.tsx
 * and surfaces toasts to the user. Must be rendered inside ToastProvider.
 */
export const ServerErrorToast: React.FC = () => {
  const toast = useToast();
  const { t } = useTranslation('shared');

  useEffect(() => {
    // TA-10138 (QA round 2): show the backend `request_id` when the event
    // carries one — the AC requires it to be visible to the user so support
    // can correlate a report with the backend trace. Network-level 5xx
    // errors have no GraphQL body, so requestId is absent there and the
    // generic copy is shown instead.
    const handler = (event: Event) => {
      const requestId = (event as CustomEvent<{ requestId?: string }>).detail
        ?.requestId;
      toast.hideAll();
      // QA round 3: a 4s toast is long enough to read "something went wrong"
      // but not to write down a reference id, which is the whole point of
      // showing it (TA-10138 AC: support correlates a user report with the
      // backend trace). Give the request_id variant a much longer window;
      // keep the generic message at its original duration.
      toast.show(
        requestId
          ? t('exceptionMsgWithRequestId', { requestId })
          : t('exceptionMsg'),
        { duration: requestId ? 15000 : 4000 }
      );
    };
    window.addEventListener('graphql-server-error', handler);
    return () => window.removeEventListener('graphql-server-error', handler);
  }, [toast, t]);

  useEffect(() => {
    const handler = () => {
      toast.hideAll();
      // duration: 0 keeps the toast visible until dismissed — appropriate for
      // a connection issue that requires user action to resolve.
      toast.show(t('connectionIssueMsg'), { duration: 0 });
    };
    window.addEventListener('graphql-circuit-breaker', handler);
    return () => window.removeEventListener('graphql-circuit-breaker', handler);
  }, [toast, t]);

  useEffect(() => {
    const handler = () => {
      toast.hideAll();
      toast.show(t('cloudflareEdgeErrorMsg'), { duration: 6000 });
    };
    window.addEventListener('graphql-cloudflare-error', handler);
    return () =>
      window.removeEventListener('graphql-cloudflare-error', handler);
  }, [toast, t]);

  // Bug fix: notifyRateLimitExceeded() (useApolloClient.tsx, TA-10142)
  // dispatches this event on web but nothing was listening for it — rate
  // limit errors were silently dropped on web.
  useEffect(() => {
    const handler = () => {
      toast.hideAll();
      toast.show(t('errors.RATE_LIMIT_EXCEEDED'), { duration: 4000 });
    };
    window.addEventListener('graphql-rate-limit', handler);
    return () => window.removeEventListener('graphql-rate-limit', handler);
  }, [toast, t]);

  // TA-10139: generic fallback for PERMISSION_DENIED/CONFLICT mutation
  // errors — see notifyMutationBusinessError in useApolloClient.tsx.
  useEffect(() => {
    const handler = (event: Event) => {
      const errorCode = (event as CustomEvent<{ errorCode: string }>).detail
        ?.errorCode;
      if (!errorCode) return;
      toast.hideAll();
      toast.show(t(`errors.${errorCode}`), { duration: 4000 });
    };
    window.addEventListener('graphql-mutation-error', handler);
    return () => window.removeEventListener('graphql-mutation-error', handler);
  }, [toast, t]);

  return null;
};
